import { useEffect, useMemo, useState } from 'react'
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { useToast } from '../../../context/useToast'
import { useWorkspace } from '../../../context/useWorkspace'
import { CreditBalance } from '../../Credits/CreditBalance'
import { formatCredits as formatCreditsShared } from '../../Credits/formatCredits'
import { useWorkspaceWalletBalance } from '../../../hooks/useCreditBalances'
import {
  executeMemberAutomationStarter,
  getMemberStarterCost,
  subscribeMemberAutomationStarters,
  type MemberAutomationStarterAction,
  type MemberAutomationStarterState,
} from '../../../services/memberControlsService'
import { isNoSignInPreviewActive } from '../../../debug'
import { testingOverrideAllowsAllFeatures } from '../../../config/testingOverrides'
import type { WorkspaceRole } from '../../../context/WorkspaceContext'
import { roleCanPerform } from '../../../utils/workspacePermissions'
import { Button } from '../../Common/Button'
import { MEMBER_QUICK_ACTIONS } from '../AutomationLauncher.helpers'
import {
  MemberActionCard,
  MemberBottomSheet,
  MemberEmptyState,
  MemberHeroHeader,
} from './MemberControlsPrimitives'

export const MemberAutomationsMobile = ({
  role,
  onOpenMembers,
}: {
  role: WorkspaceRole
  onOpenMembers?: () => void
}) => {
  const { activeWorkspaceId } = useWorkspace()
  const { showToast } = useToast()
  const wallet = useWorkspaceWalletBalance(activeWorkspaceId, isNoSignInPreviewActive() ? 5000 : 0, isNoSignInPreviewActive())
  const [states, setStates] = useState<MemberAutomationStarterState[]>([])
  const [pendingAction, setPendingAction] = useState<MemberAutomationStarterAction | null>(null)
  const [runningAction, setRunningAction] = useState<MemberAutomationStarterAction | null>(null)
  const canManage = roleCanPerform(role, 'EDITOR') || testingOverrideAllowsAllFeatures()

  useEffect(() => {
    if (!activeWorkspaceId) return
    return subscribeMemberAutomationStarters(activeWorkspaceId, setStates, (error) => showToast(error.message, 'error'))
  }, [activeWorkspaceId, showToast])

  const stateByAction = useMemo(() => new Map(states.map((entry) => [entry.actionType, entry])), [states])
  const liveActions = MEMBER_QUICK_ACTIONS.filter((action) => action.kind === 'automation' && stateByAction.get(action.id)?.status === 'ACTIVE')
  const starters = MEMBER_QUICK_ACTIONS.filter((action) => action.kind === 'automation')
  const shortcuts = MEMBER_QUICK_ACTIONS.filter((action) => action.kind === 'shortcut')

  const run = async (actionType: MemberAutomationStarterAction, mode: 'START' | 'STOP' = 'START') => {
    if (actionType === 'INVITE_MEMBER') {
      onOpenMembers?.()
      return
    }
    if (!activeWorkspaceId) return
    setRunningAction(actionType)
    try {
      const result = await executeMemberAutomationStarter({ workspaceId: activeWorkspaceId, actionType, mode })
      showToast(`${result.actionType.replaceAll('_', ' ').toLocaleLowerCase()} ${result.status.toLocaleLowerCase()}.`, 'success')
      setPendingAction(null)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Automation starter failed.', 'error')
    } finally {
      setRunningAction(null)
    }
  }

  const requestStart = (actionType: MemberAutomationStarterAction) => {
    const cost = getMemberStarterCost(actionType)
    if (cost > 0) setPendingAction(actionType)
    else void run(actionType)
  }

  const pendingCost = pendingAction ? getMemberStarterCost(pendingAction) : 0
  const pendingTitle = pendingAction ? MEMBER_QUICK_ACTIONS.find((entry) => entry.id === pendingAction)?.title ?? pendingAction : ''

  return (
    <section className="memberControlsMobile memberAutomationsMobile" data-testid="member-automation-launcher">
      <MemberHeroHeader
        eyebrow="Automations"
        title="Quick starters"
        chips={(
          <span className="memberMetricChip">
            <AccountBalanceWalletRoundedIcon fontSize="inherit" />
            <CreditBalance value={wallet.availableCredits} variant="bare" scope="workspace" label={null} />
          </span>
        )}
      />

      {liveActions.length ? (
        <section className="memberActionList" aria-label="Live automations">
          <span className="memberEyebrow">Live</span>
          {liveActions.map((action) => (
            <MemberActionCard
              key={action.id}
              icon={<span className="memberAutomationLiveDot" />}
              title={action.title}
              sub={action.description}
              status="Active"
              statusTone="success"
              footer={(
                <Button
                  variant="secondary"
                  size="md"
                  icon={<PauseRoundedIcon fontSize="small" />}
                  disabled={!canManage || runningAction !== null}
                  onClick={(event) => { event.stopPropagation(); void run(action.id, 'STOP') }}
                >
                  Stop
                </Button>
              )}
            />
          ))}
        </section>
      ) : null}

      <section className="memberActionList" aria-label="Automation starters">
        <span className="memberEyebrow">Starters</span>
        <div className="memberAutomationGrid">
          {starters.map((action) => {
            const state = stateByAction.get(action.id)
            const isActive = state?.status === 'ACTIVE'
            const cost = getMemberStarterCost(action.id)
            return (
              <MemberActionCard
                key={action.id}
                icon={action.icon}
                title={action.title}
                sub={action.description}
                status={isActive ? 'Active' : `${cost} Cr`}
                statusTone={isActive ? 'success' : 'info'}
                footer={isActive ? 'Running now' : (
                  <Button
                    variant="secondary"
                    size="md"
                    icon={<PlayArrowRoundedIcon fontSize="small" />}
                    disabled={!canManage || runningAction !== null}
                    onClick={(event) => { event.stopPropagation(); requestStart(action.id) }}
                  >
                    Start
                  </Button>
                )}
                disabled={!canManage || isActive}
              />
            )
          })}
        </div>
      </section>

      <section className="memberActionList" aria-label="Automation shortcuts">
        <span className="memberEyebrow">Shortcuts</span>
        {shortcuts.map((action) => (
          <MemberActionCard
            key={action.id}
            icon={action.icon}
            title={action.title}
            sub={action.description}
            status="Shortcut"
            statusTone="neutral"
            onClick={() => onOpenMembers?.()}
          />
        ))}
      </section>

      {!canManage ? (
        <MemberEmptyState
          icon={<PlayArrowRoundedIcon fontSize="inherit" />}
          title="Starters are restricted"
          hint="Automation starters are available to Admins and Editors."
        />
      ) : null}

      <MemberBottomSheet open={Boolean(pendingAction)} onClose={() => runningAction ? undefined : setPendingAction(null)} title="Approve starter" testId="member-automation-approve-sheet">
        <section className="memberAutomationApprove">
          <span className="memberEyebrow">Wallet</span>
          <h3>{pendingTitle}</h3>
          <dl className="memberConfirmStack">
            <div><dt>Estimated cost</dt><dd>{formatCreditsShared(pendingCost, { suffix: 'credits' })}</dd></div>
            <div><dt>Wallet balance</dt><dd><CreditBalance value={wallet.availableCredits} variant="bare" scope="workspace" label={null} /> credits</dd></div>
            <div><dt>After approval</dt><dd>{Math.max(0, wallet.availableCredits - pendingCost).toLocaleString()} credits</dd></div>
          </dl>
          <div className="memberAutomationApprove__actions">
            <Button variant="tertiary" size="md" disabled={runningAction !== null} onClick={() => setPendingAction(null)}>Cancel</Button>
            <Button variant="primary" size="lg" disabled={runningAction !== null} onClick={() => pendingAction ? void run(pendingAction) : undefined}>
              {runningAction ? 'Starting...' : 'Approve'}
            </Button>
          </div>
        </section>
      </MemberBottomSheet>
    </section>
  )
}
